import { Scale } from '../financialsScale';

const DEFAULT_SCALES: Record<Scale, number> = {
  [Scale.ONE]: 1,
  [Scale.THOUSAND]: 1e3,
  [Scale.MILLION]: 1e6,
  [Scale.BILLION]: 1e9,
  [Scale.TRILLION]: 1e12,
};

const ADJUSTED_SCALES: Record<Scale, number> = {
  [Scale.ONE]: 1,
  // Numbro starts scaling at 1000, we want to start at 100,000 (1 * 10^5)
  [Scale.THOUSAND]: 1e5,
  [Scale.MILLION]: 1e8,
  [Scale.BILLION]: 1e11,
  [Scale.TRILLION]: 1e14,
};

/**
 * Adjust the abbreviation thresholds to enforce ours
 * * Numbro has a "lowPrecision" behaviour which does some rounding before applying the threshold
 *   see (https://github.com/BenjaminVanRyseghem/numbro/issues/546#issuecomment-637666839)
 * * We generally apply different thresholds
 *   (100x the "standard", so 50,000 doesn't become 50k, but 100,000 becomes 100k)
 * @param format Should always have "a" as the abbreviation constant
 * @param value The number being formatted
 * @param scales the set of scales to use
 */
const adjustAbbreviationThresholds = (format: string, value: number, scales: Record<Scale, number>) => {
  // If already forced, don't changed
  if (format.match(/a(K|M|B|T)/)) {
    return format;
  }

  const absValue = Math.abs(value);
  if (absValue < scales[Scale.THOUSAND]) {
    // Less than thousand point, don't abbreviate
    return format.replace('a', '');
  }
  if (absValue < scales[Scale.MILLION]) {
    return format.replace('a', 'aK');
  }
  if (absValue < scales[Scale.BILLION]) {
    return format.replace('a', 'aM');
  }
  if (absValue < scales[Scale.TRILLION]) {
    return format.replace('a', 'aB');
  }
  return format.replace('a', 'aT');
};

// If we're abbreviating and were told to do multiple decimal places, reduce to 1dp
// We force exactly 1 dp when abbreviating
export const singleDecimalPlaceIfAbbreviated = (format: string) => {
  return (
    format
      // If decimals are optional when abbreviating, make them required and exactly 1 (matches "[.]")
      .replace(/(.*)(\[\.\]).*a(.*)/, '$1.0a$3')
      // If we have any decimal places when abbreviating, go to 1 required dp (matches ".0[0...]a", ".[00]a" or ".00a")
      .replace(/(.*\.).+a(.*)/, '$10a$2')
  );
};

export const customAbbreviationFormat = (format: string, value: number): string => {
  if (format.includes('a')) {
    // If we're abbreviating using the default numbro abbreviation scales
    return singleDecimalPlaceIfAbbreviated(adjustAbbreviationThresholds(format, value, DEFAULT_SCALES));
  } else if (format.includes('A')) {
    // If we're abbreviating using the custom (100x) abbreviation
    return singleDecimalPlaceIfAbbreviated(
      adjustAbbreviationThresholds(format.replace('A', 'a'), value, ADJUSTED_SCALES)
    );
  } else {
    //If we're not abbreviation, do nothing.
    return format;
  }
};
